import { useEffect, useRef, useState } from 'react';
import { Building2, X, ChevronDown } from 'lucide-react';
import { crmApi, EmpresaConta } from './crmApi';

interface Props {
  session: any;
  value: string | null | undefined;
  onChange: (id: string | null, empresa?: EmpresaConta | null) => void;
  label?: string;
  placeholder?: string;
}

const EmpresaContaPicker = ({ session, value, onChange, label = 'Empresa (Conta)', placeholder = 'Buscar empresa…' }: Props) => {
  const [empresas, setEmpresas] = useState<EmpresaConta[]>([]);
  const [carregou, setCarregou] = useState(false);
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const carregar = async () => {
    if (carregou) return;
    try {
      const lista = await crmApi.listarEmpresasConta(session);
      setEmpresas(lista);
      setCarregou(true);
    } catch { /* ignore */ }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selecionada = empresas.find(e => e.id === value);
  const filtradas = empresas.filter(e => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return e.razaoSocial.toLowerCase().includes(q) ||
      (e.nomeFantasia || '').toLowerCase().includes(q) ||
      (e.cnpj || '').includes(q);
  });

  return (
    <div ref={ref} className="relative">
      <label className="text-xs text-gray-600">{label}</label>
      {selecionada ? (
        <div className="mt-1 flex items-center gap-2 px-3 py-2 border border-blue-200 bg-blue-50 rounded-lg">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-blue-900">{selecionada.razaoSocial}</div>
            {selecionada.nomeFantasia && <div className="text-[11px] text-blue-700">{selecionada.nomeFantasia}</div>}
          </div>
          <button type="button" onClick={() => onChange(null, null)} className="p-1 rounded hover:bg-blue-100 text-blue-700">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <div className="mt-1 relative">
            <input
              value={busca}
              onChange={e => { setBusca(e.target.value); setAberto(true); }}
              onFocus={() => setAberto(true)}
              placeholder={placeholder}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm"
            />
            <ChevronDown className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          {aberto && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filtradas.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">Nenhuma empresa encontrada.</div>
              ) : (
                filtradas.slice(0, 30).map(e => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => { onChange(e.id, e); setBusca(''); setAberto(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-gray-500" /> {e.razaoSocial}
                    </div>
                    {(e.nomeFantasia || e.cnpj) && (
                      <div className="text-[11px] text-gray-500 ml-5">
                        {e.nomeFantasia}{e.nomeFantasia && e.cnpj ? ' · ' : ''}{e.cnpj}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmpresaContaPicker;
